import {
    PUBLIC_AUTH_CLIENT_ID_EN,
    PUBLIC_AUTH_CLIENT_ID_SV,
    PUBLIC_AUTH_ISSUER
} from '$env/static/public';
import { browser } from '$app/environment';
import * as oauth2 from 'oauth4webapi';
import { DateTime } from 'luxon';
import type { TFeatureFlags } from '$lib/services/legacy/iddqd/flags';
import { makeSpineAuthURL, toURL, toURLWithoutSearchParams } from './url-utils';
import type { SpineAuthUrlOptions, TokenSet } from './types';
import { TEST_AUTH_ISSUER } from '$lib/services/legacy/standaloneRegistration/constants';
import { isTest } from '$lib/services/environment';

type PatchedOpenIDTokenEndpointResponse =
    | (oauth2.OpenIDTokenEndpointResponse & { refresh_expires_in?: number })
    | oauth2.OAuth2Error;

type PatchedTokenEndpointResponse =
    | (oauth2.TokenEndpointResponse & { refresh_expires_in?: number })
    | oauth2.OAuth2Error;

type TokenEndpointResponseShape = {
    id_token?: string;
    access_token?: string;
    expires_in?: number;
    refresh_token?: string;
    refresh_expires_in?: number;
};

type KeycloakConfig = { issuer: string; clientId: string; params?: Record<string, string> };

class KeycloakMarketConfigs {
    private FALLBACK_MARKET_CONFIG = {
        issuer: PUBLIC_AUTH_ISSUER,
        clientId: PUBLIC_AUTH_CLIENT_ID_EN
    };
    private perMarketConfig: Map<string, KeycloakConfig> = new Map();
    constructor(
        forwardedHost?: string | null,
        featureFlags?: TFeatureFlags,
        isNative?: string | null
    ) {
        this.perMarketConfig.set('en', this.FALLBACK_MARKET_CONFIG);
        const svConfig: KeycloakConfig = {
            issuer: forwardedHost && isTest(forwardedHost) ? TEST_AUTH_ISSUER : PUBLIC_AUTH_ISSUER,
            clientId: PUBLIC_AUTH_CLIENT_ID_SV,
            params: {
                kc_idp_hint: 'devcode',
                scenario: 'bankid-se-spar',
                'bankid-se-version': '6',
                'bankid-se-redirect': 'true',
                ui_friendly: 'casumo-1',
                ui_locales: 'sv',
                'bankid-se-redirect-url': isNative ? 'casumo:///' : ''
            }
        };
        if (featureFlags?.fakeBankId) {
            delete svConfig.params?.['kc_idp_hint'];
        }
        this.perMarketConfig.set('sv', svConfig);
    }
    get(marketSlug: string) {
        let config = this.perMarketConfig.get(marketSlug);
        if (!config) {
            config = this.FALLBACK_MARKET_CONFIG;
        }
        return config;
    }
}

export class KeycloakClient {
    private authorizationServer?: oauth2.AuthorizationServer;
    constructor(private config: KeycloakConfig) {}
    async exchangeAuthCodeForTokens(
        authCallbackURL: URL,
        codeVerifier?: string
    ): Promise<TokenSet> {
        if (!codeVerifier) {
            throw Error('Code verifier is empty');
        }
        const authorizationServer = await this.getAuthorizationServer();
        const client = this.getClient();
        const callbackParams = oauth2.validateAuthResponse(
            authorizationServer,
            client,
            authCallbackURL.searchParams
        );
        if (oauth2.isOAuth2Error(callbackParams)) {
            throw Error(
                `Failed to validate authorization response: ${callbackParams.error}:${callbackParams.error_description}`
            );
        }
        const redirectURI = toURLWithoutSearchParams(
            authCallbackURL,
            'originPath',
            'originSearch'
        ).toString();
        const response = await oauth2.authorizationCodeGrantRequest(
            authorizationServer,
            client,
            callbackParams,
            redirectURI,
            codeVerifier
        );
        const result = (await oauth2.processAuthorizationCodeOpenIDResponse(
            authorizationServer,
            client,
            response
        )) as PatchedOpenIDTokenEndpointResponse;
        if (oauth2.isOAuth2Error(result)) {
            throw Error(
                `Failed to process authorization code: ${result.error}:${result.error_description}`
            );
        }
        return this.toTokenSet(result);
    }
    signIn(options?: SpineAuthUrlOptions) {
        if (!browser) {
            throw Error('Only supported in the browser');
        }

        const url = makeSpineAuthURL(toURL(location), 'sign-in-challenge', options).toString();

        window.location.href = url;
    }
    signOutFrontChannel(idToken?: string) {
        if (!idToken) {
            return;
        }
        const signOutURL = new URL(`${this.config.issuer}/protocol/openid-connect/logout`);
        const params = signOutURL.searchParams;
        params.append('id_token_hint', idToken);
        params.append(
            'post_logout_redirect_uri',
            makeSpineAuthURL(toURL(location), 'logout').toString()
        );
        window.location.href = signOutURL.toString();
    }
    async signOutBackChannel(idToken?: string) {
        if (!idToken) {
            return;
        }
        const signOutURL = new URL(`${this.config.issuer}/protocol/openid-connect/logout`);
        const params = signOutURL.searchParams;
        params.append('id_token_hint', idToken);
        const response = await fetch(signOutURL);
        if (!response.ok) {
            throw Error(`Could not sign out: ${response.status}:${response.statusText}`);
        }
    }
    async generateSignIn(originURL: URL) {
        const authorizationServer = await this.getAuthorizationServer();
        this.assertSupportsS256CodeChallenges(authorizationServer);
        if (!authorizationServer.authorization_endpoint) {
            throw Error('Could not get authorization endpoint');
        }
        const signInURL = new URL(authorizationServer.authorization_endpoint);
        const codeVerifier = oauth2.generateRandomCodeVerifier();
        const codeChallenge = await oauth2.calculatePKCECodeChallenge(codeVerifier);
        const params = signInURL.searchParams;
        for (const key in this.config.params) {
            params.set(key, this.config.params[key]);
        }
        params.set('client_id', this.config.clientId);
        params.set('code_challenge', codeChallenge);
        params.set('code_challenge_method', 'S256');
        params.set('redirect_uri', makeSpineAuthURL(originURL, 'callback').toString());
        params.set('response_type', 'code');
        params.set('scope', 'openid email profile');
        return { url: signInURL, codeVerifier };
    }
    async refreshTokens(tokenSet: TokenSet): Promise<TokenSet> {
        if (!tokenSet.refreshToken?.token) {
            throw Error('Unable to refresh tokens since the refresh token is missing');
        }
        const authorizationServer = await this.getAuthorizationServer();
        const client = this.getClient();
        const response = await oauth2.refreshTokenGrantRequest(
            authorizationServer,
            client,
            tokenSet.refreshToken.token
        );
        const result = (await oauth2.processRefreshTokenResponse(
            authorizationServer,
            client,
            response
        )) as PatchedTokenEndpointResponse;
        if (oauth2.isOAuth2Error(result)) {
            throw Error(`Failed to refresh tokens: ${result.error}:${result.error_description}`);
        }
        return this.toTokenSet(result);
    }
    async getJWKSetURL() {
        const jwksURI = (await this.getAuthorizationServer()).jwks_uri;
        if (!jwksURI) {
            throw Error('Unable to get the url to the JSON Web Key Set (JWKS) endpoint');
        }
        return new URL(jwksURI);
    }
    private async getAuthorizationServer() {
        if (this.authorizationServer) {
            return this.authorizationServer;
        }
        const issuerURL = new URL(this.config.issuer);
        const discoveryResponse = await oauth2.discoveryRequest(issuerURL);
        this.authorizationServer = await oauth2.processDiscoveryResponse(
            issuerURL,
            discoveryResponse
        );
        return this.authorizationServer;
    }
    private getClient(): oauth2.Client {
        return { client_id: this.config.clientId, token_endpoint_auth_method: 'none' };
    }
    private assertSupportsS256CodeChallenges(authorizationServer: oauth2.AuthorizationServer) {
        if (!authorizationServer.code_challenge_methods_supported?.includes('S256')) {
            throw Error('Authorization server does not support S256 code challenges');
        }
    }
    private toTokenSet(response: TokenEndpointResponseShape): TokenSet {
        const inSecondsToMillis = (now: DateTime, seconds: number) =>
            now.plus({ seconds }).toMillis();
        const now = DateTime.now();
        return {
            idToken: response.id_token ? { token: response.id_token } : undefined,
            accessToken:
                response.access_token && response.expires_in
                    ? {
                          token: response.access_token,
                          expiresAt: inSecondsToMillis(now, response.expires_in)
                      }
                    : undefined,
            refreshToken:
                response.refresh_token && response.refresh_expires_in
                    ? {
                          token: response.refresh_token,
                          expiresAt: inSecondsToMillis(now, response.refresh_expires_in)
                      }
                    : undefined
        };
    }
    static createForMarket(
        marketSlug: string,
        featureFlags?: TFeatureFlags,
        forwardedHost?: string | null,
        isNative?: string | null
    ) {
        return new KeycloakClient(
            new KeycloakMarketConfigs(forwardedHost, featureFlags, isNative).get(marketSlug)
        );
    }
}
